import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../context/userContext";
import { clientFetch } from "../utils/clientFetch";

export type SizeCandidatesResponse = {
  items: string[]
}

type UserSizeCandidatesStore = {
  items: string[]
  isLoading: boolean
  hasLoaded: boolean
}

const INITIAL_STORE: UserSizeCandidatesStore = {
  items: [],
  isLoading: false,
  hasLoaded: false
}

let store = INITIAL_STORE
let fetchPromise: Promise<string[]> | null = null
let requestVersion = 0
const listeners = new Set<() => void>()

const emitChange = () => {
  listeners.forEach((listener) => listener())
}

const setStore = (patch: Partial<UserSizeCandidatesStore>) => {
  store = { ...store, ...patch }
  emitChange()
}

const resetUserSizeCandidatesStore = () => {
  requestVersion += 1
  fetchPromise = null
  store = INITIAL_STORE
  emitChange()
}

const sanitizeCandidates = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  const seen = new Set<string>()
  const items: string[] = []

  for (const item of value) {
    if (typeof item !== "string") {
      continue
    }

    const trimmedItem = item.trim()
    if (trimmedItem === "" || seen.has(trimmedItem)) {
      continue
    }

    seen.add(trimmedItem)
    items.push(trimmedItem)

  }

  return items
}

const fetchUserSizeCandidates = async (forceRefresh = false): Promise<string[]> => {
  if (fetchPromise && !forceRefresh) {
    return fetchPromise
  }

  if (fetchPromise && forceRefresh) {
    try {
      await fetchPromise
    } catch (error) {
      // 先行リクエストが失敗しても強制再取得は継続する
    }
  }

  const currentRequestVersion = ++requestVersion
  setStore({ isLoading: true })

  const currentPromise = (async () => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/user/size-candidates`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: false
      })

      if (res.status === 401) {
        if (currentRequestVersion === requestVersion) {
          setStore({ items: [], isLoading: false, hasLoaded: false })
        }
        return []
      }

      if (!res.ok) {
        throw new Error(`Failed to fetch size candidates: ${res.status}`)
      }

      const data = await res.json() as { items?: unknown }
      const items = sanitizeCandidates(data.items)

      if (currentRequestVersion === requestVersion) {
        setStore({
          items,
          isLoading: false,
          hasLoaded: true
        })
      }

      return items
    } catch (error) {
      console.error("サイズ候補の取得に失敗しました。", error)
      if (currentRequestVersion === requestVersion) {
        setStore({
          isLoading: false,
          hasLoaded: false
        })
      }
      return store.items
    }
  })()

  fetchPromise = currentPromise
  try {
    return await currentPromise
  } finally {
    if (fetchPromise === currentPromise) {
      fetchPromise = null
    }
  }
}

export function useUserSizeCandidates() {
  const { isAuth, user } = useContext(UserContext)
  const [state, setState] = useState<UserSizeCandidatesStore>(store)
  const userKey = isAuth === true ? user.email.trim().toLowerCase() : ""
  const previousUserKeyRef = useRef(userKey)

  useEffect(() => {
    const listener = () => {
      setState(store)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    const hasUserChanged = previousUserKeyRef.current !== userKey
    previousUserKeyRef.current = userKey

    if (isAuth !== true || userKey === "") {
      if (store.items.length > 0 || store.isLoading || store.hasLoaded) {
        resetUserSizeCandidatesStore()
      }
      return
    }

    if (hasUserChanged) {
      resetUserSizeCandidatesStore()
    }

    if (!store.hasLoaded && !store.isLoading) {
      void fetchUserSizeCandidates()
    }
  }, [isAuth, userKey])

  const refreshCandidates = useCallback(async () => {
    if (isAuth !== true || userKey === "") {
      resetUserSizeCandidatesStore()
      return []
    }
    return fetchUserSizeCandidates(true)
  }, [isAuth, userKey])

  return {
    candidates: state.items,
    isLoading: state.isLoading,
    hasLoaded: state.hasLoaded,
    refreshCandidates
  }
}
